import { create } from 'zustand';
import axios from 'axios';

const getResponseList = (payload) => {
  const data = payload?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload)) return payload;
  return [];
};

const useOperationStore = create((set, get) => ({
  allOperations: [],
  currentOperation: null,
  personnelAccounts: [],
  currentOperationProfitability: null,
  currentOperationFinancialSummary: null,
  currentOperationPersonnelSummary: null,
  personnelSummaryError: null,

  fetchOperationPersonnelSummary: async (operationId) => {
    set({ currentOperationPersonnelSummary: null, personnelSummaryError: null });
    try {
      const res = await axios.get(`/api/operations/${operationId}/personnel-summary`);
      set({ currentOperationPersonnelSummary: res.data.data });
      return res.data.data;
    } catch (err) {
      set({ personnelSummaryError: err.response?.data?.message || 'Hak ediş özeti yüklenemedi.' });
      return null;
    }
  },

  saveStagePersonnel: async (operationId, stage, payload) => {
    try {
      const res = await axios.put(`/api/operations/${operationId}/stages/${stage}/personnel`, payload);
      return { success: true, data: res.data.data };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || 'Hak ediş kaydedilemedi.' };
    }
  },

  deleteStagePersonnel: async (operationId, stage, assignmentId) => {
    try {
      const res = await axios.delete(`/api/operations/${operationId}/stages/${stage}/personnel/${assignmentId}`);
      return { success: true, data: res.data.data };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || 'Hak ediş silinemedi.' };
    }
  },

  createStageConsumption: async (operationId, stage, payload) => {
    try {
      const res = await axios.post(`/api/operations/${operationId}/stages/${stage}/consumptions`, payload);
      return { success: true, data: res.data.data };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || 'Sarf kaydı oluşturulamadı.' };
    }
  },

  cancelStageConsumption: async (operationId, consumptionId) => {
    try {
      const res = await axios.delete(`/api/operations/${operationId}/consumptions/${consumptionId}`);
      return { success: true, data: res.data.data };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || 'Sarf kaydı iptal edilemedi.' };
    }
  },
  loading: false,
  operationFinancialSummaryLoading: false,
  error: null,

  fetchAllOperations: async (filters = {}) => {
    set({ loading: true, error: null });
    try {
      const res = await axios.get('/api/operations', { params: filters });
      const allOperations = getResponseList(res.data);
      set({ allOperations, loading: false });
      return allOperations;
    } catch (err) {
      const message = err.response?.data?.message || 'Operasyonlar yüklenemedi.';
      set({ allOperations: [], loading: false, error: message });
      return [];
    }
  },

  fetchOperationById: async (operationId) => {
    set({ loading: true, error: null });
    try {
      const res = await axios.get(`/api/operations/${operationId}`);
      const currentOperation = res.data.data || res.data || null;
      set({ currentOperation, loading: false });
      return currentOperation;
    } catch (err) {
      const message = err.response?.data?.message || 'Operasyon detayı yüklenemedi.';
      set({ currentOperation: null, loading: false, error: message });
      return null;
    }
  },

  createOperation: async (operationData) => {
    try {
      const res = await axios.post('/api/operations', operationData);
      await get().fetchAllOperations();
      return { success: true, data: res.data.data || res.data };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || err.response?.data?.error || 'Operasyon oluşturulamadı.'
      };
    }
  },

  updateOperationFields: async (operationId, payload = {}) => {
    try {
      const res = await axios.put(`/api/operations/${operationId}`, payload);
      const updatedOperation = res.data.data || res.data;

      set((state) => ({
        allOperations: (Array.isArray(state.allOperations) ? state.allOperations : []).map((operation) => (
          operation._id === operationId ? { ...operation, ...updatedOperation } : operation
        )),
        currentOperation: state.currentOperation?._id === operationId
          ? { ...state.currentOperation, ...updatedOperation }
          : state.currentOperation
      }));

      return { success: true, data: updatedOperation };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || 'Operasyon güncellenemedi.'
      };
    }
  },

  updateOperationMeasuredM3: async (operationId, measuredM3) => {
    const result = await get().updateOperationFields(operationId, {
      measuredM3: Number(measuredM3) || 0
    });

    if (!result.success) {
      return {
        success: false,
        message: result.message || 'Operasyon m³ değeri güncellenemedi.'
      };
    }

    return result;
  },

  updateOperationEstimator: async (operationId, estimator) => {
    const result = await get().updateOperationFields(operationId, {
      estimator: estimator || null
    });

    if (!result.success) {
      return {
        success: false,
        message: result.message || 'Operasyon eksperi güncellenemedi.'
      };
    }

    return result;
  },

  updateOperationStatus: async (operationId, status) => {
    try {
      const res = await axios.put(`/api/operations/${operationId}/status`, { status });
      const updatedOperation = res.data.data || res.data;

      set((state) => ({
        allOperations: (Array.isArray(state.allOperations) ? state.allOperations : []).map((operation) => (
          operation._id === operationId ? { ...operation, ...updatedOperation } : operation
        )),
        currentOperation: state.currentOperation?._id === operationId
          ? { ...state.currentOperation, ...updatedOperation }
          : state.currentOperation
      }));

      return { success: true, data: updatedOperation };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || 'Operasyon durumu güncellenemedi.'
      };
    }
  },

  assignPersonnelAccounts: async (operationId, assignments) => {
    try {
      const payload = Array.isArray(assignments) && assignments.some((item) => typeof item === 'object')
        ? { assignments }
        : { personelAccountIds: assignments };
      const res = await axios.put(`/api/operations/${operationId}/assign-personnel`, payload);
      const updatedOperation = res.data.data || res.data;

      set((state) => ({
        allOperations: (Array.isArray(state.allOperations) ? state.allOperations : []).map((operation) => (
          operation._id === operationId ? { ...operation, ...updatedOperation } : operation
        )),
        currentOperation: state.currentOperation?._id === operationId
          ? { ...state.currentOperation, ...updatedOperation }
          : state.currentOperation
      }));

      return { success: true, data: updatedOperation };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || 'Personel ataması güncellenemedi.'
      };
    }
  },

  updateOperationPersonnelWage: async (operationId, personelAccountId, newWage) => {
    try {
      const additionalPayment = Number(newWage || 0);
      const res = await axios.put(`/api/operations/${operationId}/personnel/${personelAccountId}/additional-payment`, { additionalPayment });
      const updatedOperation = res.data.data || res.data;

      set((state) => ({
        allOperations: (Array.isArray(state.allOperations) ? state.allOperations : []).map((operation) => (
          operation._id === operationId ? { ...operation, ...updatedOperation } : operation
        )),
        currentOperation: state.currentOperation?._id === operationId
          ? { ...state.currentOperation, ...updatedOperation }
          : state.currentOperation
      }));

      return { success: true, data: updatedOperation };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || 'Operasyon özel ek ödemesi güncellenemedi.'
      };
    }
  },

  fetchOperationProfitability: async (operationId) => {
    set({ loading: true, error: null });
    try {
      const res = await axios.get(`/api/finance/operation/${operationId}/profitability`);
      const rawReport = res.data.data || res.data || null;
      const toDecimal = (value) => Number(value || 0);
      const currentOperationProfitability = rawReport ? {
        ...rawReport,
        totalRevenue: toDecimal(rawReport.totalRevenue),
        invoicedExpenses: toDecimal(rawReport.invoicedExpenses),
        cashFieldExpenses: toDecimal(rawReport.cashFieldExpenses),
        personnelCost: toDecimal(rawReport.personnelCost),
        materialCost: toDecimal(rawReport.materialCost),
        personnelBaseCost: toDecimal(rawReport.personnelBaseCost),
        personnelAdditionalPayment: toDecimal(rawReport.personnelAdditionalPayment),
        netProfit: toDecimal(rawReport.netProfit)
      } : null;

      set({ currentOperationProfitability, loading: false });
      return currentOperationProfitability;
    } catch (err) {
      const message = err.response?.data?.message || 'Operasyon kârlılık raporu yüklenemedi.';
      console.error('Operasyon kârlılık raporu yüklenemedi:', err);

      const fallbackReport = {
        operationId,
        totalRevenue: 0,
        invoicedExpenses: 0,
        cashFieldExpenses: 0,
        personnelCost: 0,
        materialCost: 0,
        personnelBaseCost: 0,
        personnelAdditionalPayment: 0,
        netProfit: 0,
        counts: {
          salesInvoices: 0,
          purchaseInvoices: 0,
          cashFieldPayments: 0,
          materialConsumptions: 0
        }
      };

      set({ currentOperationProfitability: fallbackReport, loading: false, error: message });
      return fallbackReport;
    }
  },

  fetchOperationFinancialSummary: async (operationId) => {
    set({ operationFinancialSummaryLoading: true, currentOperationFinancialSummary: null, error: null });
    try {
      const res = await axios.get(`/api/operations/${operationId}/financial-summary`);
      const summary = res.data.data || res.data || null;
      set({
        currentOperationFinancialSummary: summary,
        operationFinancialSummaryLoading: false
      });
      return summary;
    } catch (err) {
      const message = err.response?.data?.message || 'Operasyon finans özeti yüklenemedi.';
      set({
        currentOperationFinancialSummary: null,
        operationFinancialSummaryLoading: false,
        error: message
      });
      return null;
    }
  },

  updateInvoiceOperation: async (invoiceId, operationId) => {
    try {
      const url = operationId
        ? `/api/operations/${operationId}/invoices/${invoiceId}/attach`
        : `/api/operations/${get().currentOperation?._id}/invoices/${invoiceId}/detach`;
      const res = await axios.put(url);
      return {
        success: true,
        data: res.data
      };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || 'Fatura operasyon ilişkisi güncellenemedi.'
      };
    }
  },

  fetchPersonnelAccounts: async (filters = {}) => {
    set({ loading: true, error: null });
    try {
      const res = await axios.get('/api/accounts', {
        params: {
          ...filters,
          type: 'EMPLOYEE',
          viewType: filters.viewType || 'all',
          limit: filters.limit || 100
        }
      });
      const personnelAccounts = res.data.data || res.data || [];
      set({ personnelAccounts, loading: false });
      return personnelAccounts;
    } catch (err) {
      const message = err.response?.data?.message || 'Personeller yüklenemedi.';
      set({ personnelAccounts: [], loading: false, error: message });
      return [];
    }
  }
}));

export default useOperationStore;
